import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { AppRole, ROLE } from '../constants/roles';

type CompanyMapping = {
    companyId?: number;
    id?: number;
    roles?: AppRole | AppRole[] | null;
};

export const isRLBillingAdminRole = (
    role: AppRole | null | undefined
): boolean => {
    if (!role) {
        return false;
    }
    const name = role.roleName?.trim().toLowerCase();
    return name === ROLE.RL_BILLING_ADMIN.roleName.toLowerCase();
};

export const hasRLBillingAdminRole = (
    roles: AppRole | AppRole[] | null | undefined
): boolean => {
    if (!roles) {
        return false;
    }
    const roleList = Array.isArray(roles) ? roles : [roles];
    return roleList.some(isRLBillingAdminRole);
};

export const getRLBillingAdminCompanyIds = (
    companyList: CompanyMapping[] | null | undefined
): number[] => {
    if (!companyList?.length) {
        return [];
    }
    return companyList.reduce<number[]>((ids, company) => {
        if (!hasRLBillingAdminRole(company?.roles)) {
            return ids;
        }

        const companyId = company.companyId ?? company.id;
        if (companyId != null && !ids.includes(companyId)) {
            ids.push(companyId);
        }

        return ids;
    }, []);
};

/**
 * Company IDs from the logged-in user's mappings that include
 * the "RL Billing Admin" role.
 */
export const useRLBillingAdmin = () => {
    const companyList = useSelector(
        (state: RootState) => state.sideBarReducer.companyList
    );
    const companyDetails: any = useSelector(
        (state: RootState) => state.sideBarReducer.companyDetails
    );

    const billingAdminCompanies = useMemo(() => {
        if (!companyList?.length) {
            return [];
        }
        return companyList.filter((company: CompanyMapping) =>
            hasRLBillingAdminRole(company?.roles)
        );
    }, [companyList]);

    const billingAdminCompanyIds = useMemo(
        () => getRLBillingAdminCompanyIds(companyList),
        [companyList]
    );

    const isRLBillingAdmin = hasRLBillingAdminRole(companyDetails?.roles);

    return {
        billingAdminCompanyIds,
        billingAdminCompanies,
        isRLBillingAdmin,
        hasBillingAdminRole: billingAdminCompanyIds.length > 0,
    };
};

export default useRLBillingAdmin;
